import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { getApiUrl } from "@/lib/api";

// Create auth context
const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = known
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get API URL at runtime
  const API_URL = getApiUrl();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it directly
    if (location.state?.user) {
      console.log("ProtectedRoute using location.state.user:", location.state.user);
      setUser(location.state.user);
      setIsAuthenticated(true);
      setIsLoading(false);
      // Clear the state to prevent stale data on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    // Otherwise, verify session with backend
    const checkAuth = async () => {
      try {
        // First try with cookie
        let response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
        });

        // If cookie auth fails, try localStorage token (mobile fallback)
        if (!response.ok) {
          const storedToken = localStorage.getItem('session_token');
          if (storedToken) {
            response = await fetch(`${API_URL}/api/auth/me`, {
              headers: {
                'Authorization': `Bearer ${storedToken}`
              }
            });
          }
        }

        if (!response.ok) {
          throw new Error("Not authenticated");
        }

        const userData = await response.json();
        console.log("ProtectedRoute /auth/me response:", userData);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('session_token'); // Clear invalid token
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.state?.user]);

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('session_token'); // Clear localStorage token
      setUser(null);
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
    }
  };

  const refreshUser = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated === false) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading while checking auth
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - will redirect via useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Authenticated - render children
  return children;
}
